import React from "react";

/*
 * The scene on the sign-in brand panel: a small neighbourhood on a lawn, led
 * by the logo's house (gold roof, 2 x 2 window), with a listing pin over it
 * and a new lead arriving. Flat, solid shapes only, so it stays crisp at any
 * size. Colours come from Login.css and mix the panel's own green, so it
 * follows the theme. Decorative: the pitch beside it says the same.
 */
export default function LoginIllustration() {
  return (
    <svg
      className="login-art-svg"
      viewBox="40 56 440 316"
      preserveAspectRatio="xMidYMid meet"
      aria-hidden="true"
      focusable="false"
    >
      <circle className="login-art-disk" cx="260" cy="214" r="150" />

      {/* Sun behind a cloud */}
      <circle className="login-art-gold" cx="178" cy="112" r="24" />
      <g className="login-art-cloud">
        <rect x="150" y="118" width="74" height="20" rx="10" />
        <circle cx="176" cy="118" r="14" />
        <circle cx="199" cy="121" r="10" />
      </g>

      {/* Lawn */}
      <ellipse className="login-art-lawn-edge" cx="260" cy="332" rx="212" ry="30" />
      <ellipse className="login-art-lawn" cx="260" cy="322" rx="212" ry="30" />

      {/* Towers in the distance */}
      <rect className="login-art-far" x="150" y="142" width="42" height="164" />
      <g className="login-art-far-window">
        <rect x="158" y="154" width="9" height="11" />
        <rect x="175" y="154" width="9" height="11" />
        <rect x="158" y="174" width="9" height="11" />
        <rect x="175" y="174" width="9" height="11" />
        <rect x="158" y="194" width="9" height="11" />
        <rect x="175" y="194" width="9" height="11" />
        <rect x="158" y="214" width="9" height="11" />
        <rect x="175" y="214" width="9" height="11" />
      </g>
      <rect className="login-art-far" x="326" y="168" width="44" height="138" />
      <g className="login-art-far-window">
        <rect x="334" y="180" width="9" height="11" />
        <rect x="352" y="180" width="9" height="11" />
        <rect x="334" y="200" width="9" height="11" />
        <rect x="352" y="200" width="9" height="11" />
      </g>

      {/* Apartment block, a few windows lit */}
      <rect className="login-art-wall-2" x="88" y="178" width="82" height="142" />
      <rect className="login-art-wall-3" x="158" y="178" width="12" height="142" />
      <rect className="login-art-wall-3" x="84" y="172" width="90" height="8" rx="2" />
      <g className="login-art-ink">
        <rect x="98" y="192" width="14" height="14" />
        <rect x="118" y="192" width="14" height="14" />
        <rect x="138" y="192" width="14" height="14" />
        <rect x="98" y="216" width="14" height="14" />
        <rect x="138" y="216" width="14" height="14" />
        <rect x="98" y="240" width="14" height="14" />
        <rect x="118" y="240" width="14" height="14" />
        <rect x="118" y="264" width="14" height="14" />
        <rect x="138" y="264" width="14" height="14" />
        <rect x="114" y="292" width="20" height="28" />
      </g>
      <g className="login-art-gold">
        <rect x="118" y="216" width="14" height="14" />
        <rect x="138" y="240" width="14" height="14" />
        <rect x="98" y="264" width="14" height="14" />
        <rect x="108" y="286" width="32" height="5" rx="1.5" />
      </g>

      {/* The house next door */}
      <path className="login-art-wall-2" d="M320 278 L356 242 L392 278 V320 H320 Z" />
      <path className="login-art-wall-3" d="M320 278 L356 242 L392 278 V284 L356 248 L320 284 Z" />
      <path className="login-art-leaf" d="M308 278 L356 230 L404 278 H392 L356 242 L320 278 Z" />
      <g className="login-art-ink">
        <rect x="346" y="256" width="9" height="9" />
        <rect x="357" y="256" width="9" height="9" />
        <rect x="346" y="267" width="9" height="9" />
        <rect x="357" y="267" width="9" height="9" />
        <rect x="348" y="292" width="16" height="28" />
      </g>

      {/* The house from the logo */}
      <path className="login-art-wall" d="M200 256 L250 206 L300 256 V320 H200 Z" />
      <rect className="login-art-wall-3" x="214" y="196" width="14" height="44" />
      <rect className="login-art-wall-2" x="210" y="191" width="22" height="7" rx="1.5" />
      <path className="login-art-wall-2" d="M200 256 L250 206 L300 256 V263 L250 213 L200 263 Z" />
      <path className="login-art-gold" d="M184 258 L250 192 L316 258 H302 L250 206 L198 258 Z" />
      <g className="login-art-ink">
        <rect x="237" y="226" width="12" height="12" />
        <rect x="251" y="226" width="12" height="12" />
        <rect x="237" y="240" width="12" height="12" />
        <rect x="251" y="240" width="12" height="12" />
        <rect x="210" y="278" width="9" height="9" />
        <rect x="221" y="278" width="9" height="9" />
        <rect x="210" y="289" width="9" height="9" />
        <rect x="221" y="289" width="9" height="9" />
        <rect x="270" y="278" width="9" height="9" />
        <rect x="281" y="278" width="9" height="9" />
        <rect x="270" y="289" width="9" height="9" />
        <rect x="281" y="289" width="9" height="9" />
        <path d="M238 320 V290 A12 12 0 0 1 262 290 V320 Z" />
      </g>
      <circle className="login-art-gold" cx="256" cy="304" r="1.8" />
      <rect className="login-art-wall-3" x="232" y="318" width="36" height="5" rx="1.5" />
      <path
        className="login-art-path"
        d="M238 323 C 236 334, 224 342, 212 351.2 A 212 30 0 0 0 288 351.7 C 276 342, 264 334, 262 323 Z"
      />

      {/* Trees and shrubs */}
      <rect className="login-art-trunk" x="418" y="288" width="6" height="32" rx="2" />
      <circle className="login-art-leaf" cx="421" cy="276" r="20" />
      <circle className="login-art-leaf-light" cx="413" cy="268" r="9" />
      <rect className="login-art-trunk" x="62" y="296" width="5" height="24" rx="2" />
      <circle className="login-art-leaf" cx="64" cy="290" r="14" />
      <circle className="login-art-leaf-light" cx="59" cy="285" r="6" />
      <g className="login-art-leaf">
        <circle cx="183" cy="316" r="10" />
        <circle cx="195" cy="319" r="7" />
        <circle cx="174" cy="320" r="6" />
        <circle cx="309" cy="317" r="9" />
        <circle cx="319" cy="320" r="6" />
        <circle cx="401" cy="318" r="8" />
        <circle cx="392" cy="321" r="5" />
      </g>
      <g className="login-art-leaf-light">
        <circle cx="180" cy="312" r="3.5" />
        <circle cx="306" cy="313" r="3" />
        <circle cx="398" cy="315" r="2.5" />
      </g>

      {/* The listing's pin, and a new lead on it */}
      <path
        className="login-art-wall"
        d="M250 180 C 246 171, 235 163, 235 151 A15 15 0 1 1 265 151 C 265 163, 254 171, 250 180 Z"
      />
      <circle className="login-art-gold" cx="250" cy="151" r="6" />
      <g className="login-art-bubble">
        <rect className="login-art-wall" x="284" y="118" width="162" height="52" rx="14" />
        <path className="login-art-wall" d="M286 136 L273 147 L286 154 Z" />
        <circle className="login-art-avatar" cx="308" cy="144" r="14" />
        <circle className="login-art-leaf" cx="308" cy="140" r="5" />
        <path className="login-art-leaf" d="M299 153 A9 8 0 0 1 317 153 Z" />
        <rect className="login-art-ink" x="330" y="134" width="70" height="8" rx="4" />
        <rect className="login-art-ink-soft" x="330" y="148" width="46" height="6" rx="3" />
        <circle className="login-art-leaf" cx="426" cy="138" r="5" />
      </g>
    </svg>
  );
}
